import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import { getCustomerId } from '@/utils/customerIdentifier'

interface DriverRatingDialogProps {
  assignmentId: string
  driverId: string
  driverName: string
  orderId: string
  branchId?: string
  existingRating?: number
  existingFeedback?: string
}

export function DriverRatingDialog({ 
  assignmentId, 
  driverId, 
  driverName, 
  orderId,
  branchId,
  existingRating = 0,
  existingFeedback = ''
}: DriverRatingDialogProps) {
  const [open, setOpen] = useState(false)
  const [rating, setRating] = useState<number>(existingRating)
  const [hoverRating, setHoverRating] = useState<number>(0)
  const [feedback, setFeedback] = useState(existingFeedback)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { i18n } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const customerId = getCustomerId()
  const isArabic = i18n.language === 'ar'

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast({
        title: 'Error',
        description: isArabic ? 'الرجاء اختيار تقييم' : 'Please select a rating',
        variant: 'destructive'
      })
      return
    }

    setIsSubmitting(true)
    try {
      const { error } = await supabase
        .from('delivery_assignments')
        .update({
          customer_rating: rating,
          customer_feedback: feedback || null
        })
        .eq('id', assignmentId)

      if (error) throw error

      toast({
        title: isArabic ? 'شكراً' : 'Thank You',
        description: isArabic ? 'تم حفظ تقييمك بنجاح' : 'Your rating has been saved'
      })

      // Invalidate and refetch orders
      queryClient.invalidateQueries({ queryKey: ['customer-orders', customerId, branchId] })
      
      setOpen(false)
    } catch (error) {
      console.error('Error submitting rating:', error)
      toast({
        title: 'Error',
        description: isArabic ? 'حدث خطأ أثناء حفظ التقييم' : 'Failed to save rating',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
          {[1, 2, 3, 4, 5].map(star => (
            <Star
              key={star}
              className={`h-4 w-4 ${
                star <= (existingRating || 0)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          ))}
          <span className="text-xs font-medium text-gray-600 ml-1">
            {existingRating > 0 ? existingRating : 'Rate'}
          </span>
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isArabic ? 'تقييم السائق' : 'Rate Driver'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Driver Name */}
          <div>
            <p className="text-sm text-gray-600">
              {isArabic ? 'السائق:' : 'Driver:'} <span className="font-semibold text-gray-800">{driverName}</span>
            </p>
          </div>

          {/* Star Rating */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              {isArabic ? 'اختر تقييماً' : 'Select a rating'}
            </p>
            <div className="flex gap-3 justify-center">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-125"
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-sm text-gray-600 mt-2">
                {isArabic
                  ? ['', 'سيء', 'مقبول', 'جيد', 'جيد جداً', 'ممتاز'][rating]
                  : ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]
                }
              </p>
            )}
          </div>

          {/* Feedback Textarea */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              {isArabic ? 'تعليق إضافي (اختياري)' : 'Additional Comments (Optional)'}
            </label>
            <Textarea
              placeholder={isArabic ? 'أخبرنا بمزيد من التفاصيل...' : 'Tell us more...'}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              {isArabic ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmitRating}
              disabled={isSubmitting || rating === 0}
              className="bg-yellow-500 hover:bg-yellow-600"
            >
              {isSubmitting ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : (isArabic ? 'إرسال التقييم' : 'Submit Rating')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
